// /src/components/ExportButton.jsx
import React, { useState } from "react";
import { exportPdf } from "../pdf/core/pdfService";
import { exportXlsx } from "../excel/core/excelService";

/**
 * Универсальная кнопка(и) экспорта: PDF (pdfmake) + Excel (.xlsx, SheetJS).
 *
 * PDF-пример:
 *   <ExportButton
 *     data={serviceData}
 *     buildDoc={(data) => ({ /* pdfmake docDefinition */ /* })}
 *     filename="my-service-report.pdf"
 *   />
 *
 * Excel-пример (параллельно PDF):
 *   <ExportButton
 *     data={serviceData}
 *     buildDoc={...} filename="my.pdf"
 *     buildWorkbook={(XLSX, data) => {
 *       const wb = XLSX.utils.book_new();
 *       const ws = XLSX.utils.json_to_sheet(data.rows || []);
 *       XLSX.utils.book_append_sheet(wb, ws, "Data");
 *       return wb;
 *     }}
 *     xlsxFilename="my-service-report.xlsx"
 *   />
 */
export default function ExportButton({
  data,
  // PDF
  buildDoc,
  filename = "report.pdf",
  // Excel
  buildWorkbook, // optional: (XLSX, data) => workbook
  xlsxFilename = "report.xlsx",
  // UI
  className = "",
  style = {},
  disabled: disabledProp = false,
  label = "Export PDF",
  labelXlsx = "Excel (.xlsx)",
  // Hooks
  onBeforeBuild, // optional async (data) => transformedData (общий для обоих экспортов)
  onAfterExport, // optional () => void
}) {
  const [busy, setBusy] = useState(false);
  const disabled = disabledProp || !data || busy;

  const handlePdf = async () => {
    if (disabled || !buildDoc) return;
    setBusy(true);
    try {
      const src = typeof onBeforeBuild === "function" ? await onBeforeBuild(data) : data;
      const docDefinition = buildDoc(src);
      if (!docDefinition || typeof docDefinition !== "object") {
        throw new Error("buildDoc() must return a pdfmake docDefinition object");
      }
      await exportPdf("pdfmake", { docDefinition, filename });
      if (typeof onAfterExport === "function") onAfterExport();
    } catch (e) {
      console.error("PDF export failed:", e);
      alert("PDF export failed: " + (e?.message || e));
    } finally {
      setBusy(false);
    }
  };

  const handleXlsx = async () => {
    if (disabled || typeof buildWorkbook !== "function") return;
    setBusy(true);
    try {
      const src = typeof onBeforeBuild === "function" ? await onBeforeBuild(data) : data;
      await exportXlsx({
        filename: xlsxFilename,
        buildWorkbook: async (XLSX) => {
          const wb = await Promise.resolve(buildWorkbook(XLSX, src));
          if (!wb || typeof wb !== "object") {
            throw new Error("buildWorkbook() must return a SheetJS workbook");
          }
          return wb;
        },
      });
      if (typeof onAfterExport === "function") onAfterExport();
    } catch (e) {
      console.error("XLSX export failed:", e);
      alert("XLSX export failed: " + (e?.message || e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: "inline-flex", gap: 8 }}>
      <button
        type="button"
        className={className}
        style={style}
        disabled={disabled || !buildDoc}
        onClick={handlePdf}
      >
        {busy ? "Exporting…" : label}
      </button>

      {typeof buildWorkbook === "function" && (
        <button
          type="button"
          className={className}
          style={style}
          disabled={disabled}
          onClick={handleXlsx}
        >
          {busy ? "Exporting…" : labelXlsx}
        </button>
      )}
    </div>
  );
}
